import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Sidebar from '../../components/Sidebar';
import Header from '../../components/Header';
import { Package, TrendingUp, Shield, MapPin, Plus, Search, Filter, Download, BarChart } from 'lucide-react';
import { assetService } from '../../services/assetService';
import { userProfileService } from '../../services/userProfileService';
import { formatCurrency } from '../../utils/currencyFormatter';
import AddAssetModal from './components/AddAssetModal';
import AssetDetailsModal from './components/AssetDetailsModal';
import Papa from 'papaparse';


export default function AssetManagementOverview() {
  const navigate = useNavigate();
  const [assets, setAssets] = useState([]);
  const [statistics, setStatistics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [filterLocation, setFilterLocation] = useState('all');
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isDetailsModalOpen, setIsDetailsModalOpen] = useState(false);
  const [selectedAssetId, setSelectedAssetId] = useState(null);
  const [editingAsset, setEditingAsset] = useState(null);
  const [userCurrency, setUserCurrency] = useState('USD');

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [assetsData, statsData, profile] = await Promise.all([
        assetService?.getAll(),
        assetService?.getStatistics(),
        userProfileService?.getCurrentProfile()
      ]);
      setAssets(assetsData);
      setStatistics(statsData);
      setUserCurrency(profile?.currency || 'USD');
    } catch (err) {
      setError(err?.message);
    } finally {
      setLoading(false);
    }
  };

  const handleViewDetails = (assetId) => {
    setSelectedAssetId(assetId);
    setIsDetailsModalOpen(true);
  };

  const handleEditAsset = (asset) => {
    // Close details modal and open edit modal with asset data
    setIsDetailsModalOpen(false);
    setEditingAsset(asset);
    setIsAddModalOpen(true);
  };

  const handleDeleteAsset = () => {
    loadData(); // Refresh after delete
  };

  const handleAddAsset = () => {
    setEditingAsset(null); // Clear editing asset for new asset
    setIsAddModalOpen(true);
  };

  const handleAssetAdded = () => {
    loadData(); // Refresh the assets list
    setEditingAsset(null); // Clear editing asset
  };

  const handleModalClose = () => {
    setIsAddModalOpen(false);
    setEditingAsset(null); // Clear editing asset when modal closes
  };

  // Filter assets based on search and filters
  const filteredAssets = assets?.filter(asset => {
    const matchesSearch = asset?.assetName?.toLowerCase()?.includes(searchTerm?.toLowerCase()) ||
                         asset?.assignedStaffMember?.toLowerCase()?.includes(searchTerm?.toLowerCase()) ||
                         (asset?.serialNumber && asset?.serialNumber?.toLowerCase()?.includes(searchTerm?.toLowerCase()));
    const matchesType = filterType === 'all' || asset?.assetType === filterType;
    const matchesLocation = filterLocation === 'all' || asset?.location === filterLocation;
    return matchesSearch && matchesType && matchesLocation;
  });

  const exportToCSV = () => {
    const csvData = filteredAssets?.map(asset => ({
      'Asset Name': asset?.assetName,
      'Type': asset?.assetType,
      'Value': asset?.assetValue,
      'Currency': userCurrency,
      'Assigned To': asset?.assignedStaffMember,
      'Location': asset?.location === 'in_office' ? 'In Office' : 'Remote',
      'Insured': asset?.isInsured ? 'Yes' : 'No',
      'Serial Number': asset?.serialNumber || 'N/A',
      'Purchase Date': asset?.purchaseDate ? new Date(asset.purchaseDate)?.toLocaleDateString() : 'N/A',
      'Warranty Expiry': asset?.warrantyExpiry ? new Date(asset.warrantyExpiry)?.toLocaleDateString() : 'N/A',
      'Notes': asset?.notes || ''
    }));

    const csv = Papa?.unparse(csvData);
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link?.setAttribute('href', url);
    link?.setAttribute('download', `assets_${new Date()?.toISOString()?.split('T')?.[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body?.appendChild(link);
    link?.click();
    document.body?.removeChild(link);
  };

  if (loading) {
    return (
      <div className="flex h-screen bg-gradient-to-br from-slate-50 via-blue-50/30 to-indigo-50/40">
        <Sidebar />
        <div className="flex-1 flex flex-col ml-64">
          <Header />
          <main className="flex-1 flex items-center justify-center">
            <div className="text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
              <p className="mt-4 text-gray-600 font-medium">Loading assets...</p>
            </div>
          </main>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex h-screen bg-gradient-to-br from-slate-50 via-blue-50/30 to-indigo-50/40">
        <Sidebar />
        <div className="flex-1 flex flex-col ml-64">
          <Header />
          <main className="flex-1 flex items-center justify-center">
            <div className="text-center">
              <div className="bg-gradient-to-r from-red-50 to-red-100/50 border-l-4 border-red-500 rounded-xl p-6 max-w-md shadow-lg">
                <p className="text-red-800 font-semibold text-lg">Error loading assets</p>
                <p className="text-red-600 text-sm mt-2">{error}</p>
                <button
                  onClick={loadData}
                  className="mt-4 px-6 py-2.5 bg-gradient-to-r from-red-600 to-rose-600 text-white rounded-xl hover:from-red-700 hover:to-rose-700 transition-all shadow-lg font-semibold"
                >
                  Retry
                </button>
              </div>
            </div>
          </main>
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-screen bg-gradient-to-br from-slate-50 via-blue-50/30 to-indigo-50/40">
      <Sidebar />
      <div className="flex-1 flex flex-col ml-64">
        <Header />
        
        <main className="flex-1 overflow-auto px-6 pb-6 pt-4">
          {/* Modern Page Header with gradient */}
          <div className="bg-gradient-to-r from-blue-600 to-indigo-600 rounded-2xl shadow-xl border border-blue-400/20 p-8 mb-6 relative overflow-hidden">
            <div className="absolute top-0 right-0 w-64 h-64 bg-white/10 rounded-full -mr-32 -mt-32"></div>
            <div className="absolute bottom-0 left-0 w-48 h-48 bg-white/10 rounded-full -ml-24 -mb-24"></div>
            <div className="relative z-10 flex items-center justify-between">
              <div>
                <div className="flex items-center gap-3 mb-2">
                  <div className="p-3 bg-white/20 backdrop-blur-sm rounded-xl">
                    <Package className="w-7 h-7 text-white" />
                  </div>
                  <h1 className="text-3xl font-bold text-white">Asset Management</h1>
                </div>
                <p className="text-blue-100 text-base ml-16">
                  Track and manage your business assets with ease
                </p>
              </div>
              <div className="flex gap-3">
                <button
                  onClick={handleAddAsset}
                  className="bg-white text-blue-600 px-6 py-3 rounded-xl flex items-center gap-2 hover:bg-blue-50 transition-all shadow-lg font-semibold"
                >
                  <Plus className="w-5 h-5" />
                  Add Asset
                </button>
                <button
                  onClick={() => navigate('/asset-management-analytics')}
                  className="bg-white/10 backdrop-blur-sm text-white border-2 border-white/30 px-6 py-3 rounded-xl flex items-center gap-2 hover:bg-white/20 transition-all shadow-lg font-semibold"
                >
                  <BarChart className="w-5 h-5" />
                  Analytics
                </button>
              </div>
            </div>
          </div>

          {/* Statistics Cards with modern gradients */}
          {statistics && (
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
              <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-2xl p-5 shadow-lg border border-blue-400/20 relative overflow-hidden">
                <div className="absolute top-0 right-0 w-24 h-24 bg-white/10 rounded-full -mr-12 -mt-12"></div>
                <div className="relative z-10">
                  <div className="flex items-center gap-2 mb-2">
                    <div className="p-2 bg-white/20 backdrop-blur-sm rounded-lg">
                      <Package className="w-5 h-5 text-white" />
                    </div>
                    <div className="text-sm text-blue-100 font-medium">Total Assets</div>
                  </div>
                  <div className="text-3xl font-bold text-white">{statistics?.totalAssets}</div>
                </div>
              </div>

              <div className="bg-gradient-to-br from-green-500 to-emerald-600 rounded-2xl p-5 shadow-lg border border-green-400/20 relative overflow-hidden">
                <div className="absolute top-0 right-0 w-24 h-24 bg-white/10 rounded-full -mr-12 -mt-12"></div>
                <div className="relative z-10">
                  <div className="flex items-center gap-2 mb-2">
                    <div className="p-2 bg-white/20 backdrop-blur-sm rounded-lg">
                      <TrendingUp className="w-5 h-5 text-white" />
                    </div>
                    <div className="text-sm text-green-100 font-medium">Total Value</div>
                  </div>
                  <div className="text-3xl font-bold text-white">
                    {formatCurrency(statistics?.totalValue, userCurrency)}
                  </div>
                </div>
              </div>

              <div className="bg-gradient-to-br from-purple-500 to-purple-600 rounded-2xl p-5 shadow-lg border border-purple-400/20 relative overflow-hidden">
                <div className="absolute top-0 right-0 w-24 h-24 bg-white/10 rounded-full -mr-12 -mt-12"></div>
                <div className="relative z-10">
                  <div className="flex items-center gap-2 mb-2">
                    <div className="p-2 bg-white/20 backdrop-blur-sm rounded-lg">
                      <Shield className="w-5 h-5 text-white" />
                    </div>
                    <div className="text-sm text-purple-100 font-medium">Insured Assets</div>
                  </div>
                  <div className="text-3xl font-bold text-white">
                    {statistics?.insuredCount}/{statistics?.totalAssets}
                  </div>
                </div>
              </div>

              <div className="bg-gradient-to-br from-orange-500 to-orange-600 rounded-2xl p-5 shadow-lg border border-orange-400/20 relative overflow-hidden">
                <div className="absolute top-0 right-0 w-24 h-24 bg-white/10 rounded-full -mr-12 -mt-12"></div>
                <div className="relative z-10">
                  <div className="flex items-center gap-2 mb-2">
                    <div className="p-2 bg-white/20 backdrop-blur-sm rounded-lg">
                      <MapPin className="w-5 h-5 text-white" />
                    </div>
                    <div className="text-sm text-orange-100 font-medium">Location Split</div>
                  </div>
                  <div className="text-3xl font-bold text-white">
                    {statistics?.inOfficeCount}/{statistics?.remoteCount}
                  </div>
                  <p className="text-xs text-orange-100 mt-1">Office/Remote</p>
                </div>
              </div>
            </div>
          )}

          {/* Enhanced Search and Filter */}
          <div className="bg-white rounded-2xl shadow-lg border border-gray-200/60 p-6 mb-6">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                <input
                  type="text"
                  placeholder="Search assets..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e?.target?.value)}
                  className="w-full pl-10 pr-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
                />
              </div>

              <div className="relative">
                <Filter className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                <select
                  value={filterType}
                  onChange={(e) => setFilterType(e?.target?.value)}
                  className="w-full pl-10 pr-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 appearance-none transition-all"
                >
                  <option value="all">All Types</option>
                  <option value="computer">Computer</option>
                  <option value="furniture">Furniture</option>
                  <option value="equipment">Equipment</option>
                  <option value="software">Software</option>
                  <option value="other">Other</option>
                </select>
              </div>

              <div className="relative">
                <MapPin className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                <select
                  value={filterLocation}
                  onChange={(e) => setFilterLocation(e?.target?.value)}
                  className="w-full pl-10 pr-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 appearance-none transition-all"
                >
                  <option value="all">All Locations</option>
                  <option value="in_office">In Office</option>
                  <option value="remote">Remote</option>
                </select>
              </div>

              <button
                onClick={exportToCSV}
                className="flex items-center justify-center gap-2 px-6 py-3 bg-gradient-to-r from-green-600 to-emerald-600 text-white rounded-xl hover:from-green-700 hover:to-emerald-700 transition-all shadow-lg font-semibold"
              >
                <Download className="w-5 h-5" />
                Export CSV
              </button>
            </div>
          </div>

          {/* Enhanced Assets Table */}
          <div className="bg-white rounded-2xl shadow-lg border border-gray-200/60 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gradient-to-r from-slate-50 to-gray-50 border-b-2 border-gray-200">
                  <tr>
                    <th className="px-6 py-4 text-left text-sm font-bold text-slate-900">
                      Asset Name
                    </th>
                    <th className="px-6 py-4 text-left text-sm font-bold text-slate-900">
                      Type
                    </th>
                    <th className="px-6 py-4 text-left text-sm font-bold text-slate-900">
                      Value
                    </th>
                    <th className="px-6 py-4 text-left text-sm font-bold text-slate-900">
                      Assigned To
                    </th>
                    <th className="px-6 py-4 text-left text-sm font-bold text-slate-900">
                      Location
                    </th>
                    <th className="px-6 py-4 text-left text-sm font-bold text-slate-900">
                      Insured
                    </th>
                    <th className="px-6 py-4 text-left text-sm font-bold text-slate-900">
                      Warranty
                    </th>
                    <th className="px-6 py-4 text-left text-sm font-bold text-slate-900">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {filteredAssets?.length === 0 ? (
                    <tr>
                      <td colSpan="8" className="px-6 py-16 text-center">
                        <div className="inline-flex p-4 bg-gray-100 rounded-full mb-4">
                          <Package className="w-12 h-12 text-gray-400" />
                        </div>
                        <p className="text-gray-500 text-lg font-medium">
                          {searchTerm || filterType !== 'all' || filterLocation !== 'all' ?'No assets found matching your filters' :'No assets yet. Click "Add Asset" to get started.'}
                        </p>
                      </td>
                    </tr>
                  ) : (
                    filteredAssets?.map((asset) => (
                      <tr key={asset?.id} className="hover:bg-blue-50/50 transition-colors">
                        <td className="px-6 py-5 whitespace-nowrap">
                          <div className="text-sm font-semibold text-gray-900">{asset?.assetName}</div>
                          {asset?.serialNumber && (
                            <div className="text-xs text-gray-500 mt-1">{asset?.serialNumber}</div>
                          )}
                        </td>
                        <td className="px-6 py-5 whitespace-nowrap">
                          <span className="px-3 py-1.5 inline-flex text-xs leading-5 font-semibold rounded-full bg-blue-100 text-blue-800">
                            {asset?.assetType}
                          </span>
                        </td>
                        <td className="px-6 py-5 whitespace-nowrap text-sm font-medium text-gray-900">
                          {formatCurrency(asset?.assetValue, userCurrency)}
                        </td>
                        <td className="px-6 py-5 whitespace-nowrap text-sm text-gray-900">
                          {asset?.assignedStaffMember}
                        </td>
                        <td className="px-6 py-5 whitespace-nowrap">
                          <span className={`px-3 py-1.5 inline-flex text-xs leading-5 font-semibold rounded-full ${
                            asset?.location === 'in_office' ?'bg-green-100 text-green-800' :'bg-orange-100 text-orange-800'
                          }`}>
                            {asset?.location === 'in_office' ? 'In Office' : 'Remote'}
                          </span>
                        </td>
                        <td className="px-6 py-5 whitespace-nowrap">
                          {asset?.isInsured ? (
                            <span className="text-green-600 flex items-center gap-1.5 font-medium">
                              <Shield className="w-4 h-4" />
                              Yes
                            </span>
                          ) : (
                            <span className="text-gray-400">No</span>
                          )}
                        </td>
                        <td className="px-6 py-5 whitespace-nowrap text-sm text-gray-900">
                          {asset?.warrantyExpiry 
                            ? new Date(asset.warrantyExpiry)?.toLocaleDateString()
                            : 'N/A'}
                        </td>
                        <td className="px-6 py-5 whitespace-nowrap text-sm">
                          <button
                            onClick={() => handleViewDetails(asset?.id)}
                            className="text-sm bg-blue-50 text-blue-600 px-4 py-2 rounded-lg hover:bg-blue-100 transition-colors font-semibold"
                          >
                            View Details
                          </button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </main>
      </div>

      {/* Add Asset Modal */}
      <AddAssetModal 
        isOpen={isAddModalOpen}
        onClose={handleModalClose}
        onAssetAdded={handleAssetAdded}
        editingAsset={editingAsset}
      />

      {/* Asset Details Modal */}
      <AssetDetailsModal 
        isOpen={isDetailsModalOpen}
        onClose={() => setIsDetailsModalOpen(false)}
        assetId={selectedAssetId}
        userCurrency={userCurrency}
        onEdit={handleEditAsset}
        onDelete={handleDeleteAsset}
      />
    </div>
  );
}